<script setup lang="ts">
import { nextTick, onMounted, provide, ref, watch } from 'vue';
import CsvUpload from '@/csv/components/CsvUpload/CsvUpload.vue';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { instanceOfCsvPostAssertErrorResponse } from '@/apis/generated/dialbunny';
import type { Csv } from '@/apis/generated/dialbunny';
import { ResponseError } from '@/apis/generated/common/runtime';
import { useCsvStore } from '@/stores';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DATA_QA } from './csv-import-upload-step.constants';
import logger from '@/logger';

const log = logger.getChild('CsvImportUploadStep');

log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const csv = defineModel<Csv | null>({
  required: true,
});

const emit = defineEmits({
  'update:examples': (examples: string[]) => true,
  continue: () => true,
});

const file = ref<File | null>(null);
const errors = ref(new Map<string, string | null>());
const loading = ref(false);
const validationTrigger = ref(0);
const csvStore = useCsvStore();
const csvUploadRef = ref<InstanceType<typeof CsvUpload> | null>(null);

provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const setError = (key: string, error: string | null) => {
  errors.value.set(key, error);
};

/**
 * Clear the unknown error from the error map.
 * Unknown error should not block the user from proceeding.
 */
const clearUnknownError = () => {
  if (errors.value.get('file') === 'UNKNOWN') {
    log.debug('Clearing unknown error');
    errors.value.set('file', null);
  }
};

const validate = async (): Promise<boolean> => {
  log.debug('Validating form');
  clearUnknownError();
  validationTrigger.value++;

  await nextTick();

  const isValid = [...errors.value.values()].every((error) => error === null);

  log.debug('Validation result:', isValid);

  return isValid;
};

const handleCreateError = async (catchError: unknown) => {
  log.debug('Handling create error:', catchError);
  if (!(catchError instanceof ResponseError)) {
    // show toast banner
    log.error(
      'Unexpected error type:',
      catchError instanceof Error ? catchError.message : catchError,
    );
    return;
  }

  const json = await catchError.response.json();

  if (!instanceOfCsvPostAssertErrorResponse(json)) {
    // show toast banner
    log.error('Unexpected error type in response:', json);
    return;
  }

  csvUploadRef.value?.setError(json.type);
};

const submit = async () => {
  log.debug('Submitting');
  const csvToUpload = file.value;
  if (!(await validate()) || !csvToUpload) {
    return;
  }

  if (!csv.value) {
    loading.value = true;
    log.debug('Creating csv record...');
    csvStore.create(
      csvToUpload,
      {
        onSuccess: (created) => {
          csv.value = created;
          log.debug('Create successful');
          loading.value = false;
          emit('continue');
        },
        onRetry: () => {
          // show toast
        },
        onError: async (error) => {
          await handleCreateError(error);
          loading.value = false;
        },
      },
      false,
    );
  } else {
    emit('continue');
  }
};

watch([file], () => {
  csv.value = null;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <CsvUpload
      ref="csvUploadRef"
      v-model="file"
      required
      @update:examples="($event) => emit('update:examples', $event)"
      @update:error="($event) => setError('file', $event)"
    />
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CONTINUE" :loading="loading" @click="submit">
        {{ $t('CSV_IMPORT_CONTINUE') }}
      </DtButton>
    </div>
  </div>
</template>
